import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SyncRow {
  readonly syncJobId: string;
  readonly syncName: string;
  readonly entityName: string;
  readonly syncMode: string;
  readonly status: string;
}

export function M20SyncMonitoringPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<SyncRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = (): void => {
    apiRequest<{ data: SyncRow[] }>('/v1/integration/sync-jobs', { token })
      .then((r) => setRows(r.data))
      .catch((e) => setError((e as Error).message));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const act = async (id: string, action: 'run' | 'stop'): Promise<void> => {
    setBusyId(id + action);
    try {
      await apiRequest(`/v1/integration/sync-jobs/${id}/${action}`, {
        method: 'POST', token, body: action === 'run' ? {} : undefined,
      });
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m20.syncMonitoring.loading')}</p>;
  return (
    <main aria-label="sync-monitoring">
      <h1>{t('m20.syncMonitoring.title')}</h1>
      <table aria-label="sync-jobs-table">
        <thead><tr><th>{t('m20.syncMonitoring.colName')}</th><th>{t('m20.syncMonitoring.colEntity')}</th><th>{t('m20.syncMonitoring.colMode')}</th><th>{t('m20.syncMonitoring.colStatus')}</th><th>{t('m20.syncMonitoring.colActions')}</th></tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.syncJobId}>
              <td>{s.syncName}</td>
              <td>{s.entityName}</td>
              <td>{s.syncMode}</td>
              <td>{s.status}</td>
              <td>
                <button type="button" aria-label={`run-${s.syncJobId}`} disabled={busyId === s.syncJobId + 'run'} onClick={() => void act(s.syncJobId, 'run')}>{t('m20.syncMonitoring.run')}</button>
                <button type="button" aria-label={`stop-${s.syncJobId}`} disabled={busyId === s.syncJobId + 'stop'} onClick={() => void act(s.syncJobId, 'stop')}>{t('m20.syncMonitoring.stop')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
